import { ColumnDef } from '@tanstack/react-table';
import { Translator } from '../translator.type';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { format } from 'date-fns';

export type EditableCircuit = {
  id: string;
  name: string;
  version: number;
  title?: string;
  createdAt: string;
  updatedAt: string;
  countries: string[];
  durationDays: number;
  durationNights: number;
  originalCircuit: {
    id: string;
    code: number;
    updatedAt: string;
    company: {
      name: string;
    };
    sharedCircuits: {
      distributedAt: string | null;
    }[];
  };
};

export const editableCircuitColumns = (
  t: Translator,
): ColumnDef<EditableCircuit>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
  },
  {
    accessorKey: 'code',
    header: t('distributorCircuits.list.undistributedCircuits.columns.code'),
    cell: ({ row }) =>
      row.original.originalCircuit.code && (
        <span>{`C${row.original.originalCircuit.code.toString().padStart(4, '0')}-${row.original.version}`}</span>
      ),
  },
  {
    accessorKey: 'name',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.circuitName',
    ),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis"
            aria-label="nom"
          >
            {row.original.title ?? row.original.name}
          </TooltipTrigger>
          <TooltipContent
            className="bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg"
            aria-label="nom complet"
          >
            {row.original.title ?? row.original.name}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: 'originalCircuit.company.name',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.receiver',
    ),
  },
  {
    accessorKey: 'countries',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.countries',
    ),
    cell: ({ row }) => <span>{row.original.countries.join(', ')}</span>,
  },
  {
    accessorKey: 'durationDays',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.duration',
    ),
    cell: ({ row }) =>
      row.original.durationDays ? (
        <span>
          {row.original.durationDays}j
          {row.original.durationNights && ` / ${row.original.durationNights}n`}
        </span>
      ) : null,
  },
  {
    accessorKey: 'createdAt',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.createdAt',
    ),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.createdAt), 'dd/MM/yyyy')}</span>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.updatedAt',
    ),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.updatedAt), 'dd/MM/yyyy')}</span>
    ),
  },
  {
    accessorKey: 'originalCircuit.updatedAt',
    header: t(
      'distributorCircuits.list.undistributedCircuits.columns.originalUpdatedAt',
    ),
    cell: ({ row }) => (
      <span>
        {format(new Date(row.original.originalCircuit.updatedAt), 'dd/MM/yyyy')}
      </span>
    ),
  },
];
